<script setup lang="ts">
import { useGlobalUser } from "@/stores/globalUser";
import { useInteractive } from "@/stores/interactive";
import { useMate } from "@/stores/mate";
import { useUI } from "@/stores/ui";
import { useLocalStorage } from "@vueuse/core";
import moment from "moment";
import { storeToRefs } from "pinia";
import { Popup } from "vant";
import { watch } from "vue";
import { ref } from "vue";
import { useRouter } from "vue-router";

interface JoinInteractivePoolInformCache {
  lastShowTime: number | null;
  neverShow: boolean;
}
// States
const show = ref(false);
const userStore = useGlobalUser();
const interactiveStore = useInteractive();
const mateStore = useMate();
const uiStore = useUI();
const { poolList } = storeToRefs(interactiveStore);
const router = useRouter();

const informCache = useLocalStorage<JoinInteractivePoolInformCache>(
  "joinInteractivePoolInform",
  {
    lastShowTime: null,
    neverShow: false,
  }
);
// Lifecycle
const isDateChanged = (lastShowTime: number | null) => {
  if (lastShowTime === null) return true;
  const lastShowDay = moment(lastShowTime).day();
  const today = moment().day();
  return lastShowDay !== today;
};

watch(
  () => userStore.isLogin,
  async (isLogin) => {
    // 1. 未登录 || 不再提醒 => 不显示
    if (!isLogin || informCache.value.neverShow) return;
    // 2. 互动池有数据 => 不显示
    if (poolList.value.length > 0) return;
    await interactiveStore.getPoolList();
    if (poolList.value.length > 0) return;
    // 3. 没有OC => 不显示
    if (!mateStore.list.filter((v) => v.status === 1).length) return;
    // 4. 未显示过 || （上次点击下次再说）日期改变 => 显示
    if (
      informCache.value.lastShowTime === null ||
      isDateChanged(informCache.value.lastShowTime)
    ) {
      show.value = true;
    }
  },
  { immediate: true }
);

// Methods
const showOnNextDay = () => {
  informCache.value.lastShowTime = new Date().getTime();
  show.value = false;
};

const onSelectOC = () => {
  uiStore.popInteractivePoolMateSelector(() => {
    show.value = false;
    router.push({ name: "interactive" });
  });
};
</script>
<template>
  <Popup
    :show="show"
    @update:show="show = $event"
    round
    :close-on-click-overlay="false"
  >
    <div class="p-8 flex flex-col gap-5">
      <h1 class="font-bold text-lg">
        {{ $t("interactive.joinInform.title") }}
      </h1>
      <div class="flex flex-col gap-1">
        <div class="flex gap-1">
          <i class="iconify mingcute--check-circle-line text-success" />
          OC主动结识更多好友
        </div>
        <div class="flex gap-1">
          <i class="iconify mingcute--check-circle-line text-success" />
          OC主动发起写信邀请
        </div>
        <div class="flex gap-1">
          <i class="iconify mingcute--check-circle-line text-success" />
          OC发起饼友圈互动
        </div>
      </div>
      <div class="flex gap-2 pr-8">
        <button
          class="btn btn-primary btn-sm rounded flex-1"
          @click="onSelectOC"
        >
          {{ $t("interactive.joinInform.selectOC") }}
        </button>
        <button
          class="btn btn-outline btn-sm rounded flex-1"
          @click="showOnNextDay"
        >
          {{ $t("interactive.joinInform.nextDay") }}
        </button>
      </div>
      <div class="flex gap-1 items-center">
        <input
          type="checkbox"
          class="checkbox checkbox-sm rounded"
          v-model="informCache.neverShow"
        />
        <span class="text-base-content/50">
          {{ $t("interactive.joinInform.neverShow") }}
        </span>
      </div>
    </div>
  </Popup>
</template>
